import React from 'react';

import { Grid, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountPrefTypeOptions, CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { InlineRichOptionSelect } from '../InlineRichOptionSelect';

export interface CreateAccountContactFieldsProps extends CreateAccountSectionProps {
  prefTypeOptions?: CreateAccountPrefTypeOptions;
}

export const CreateAccountContactFields: React.FC<
  CreateAccountContactFieldsProps
> = ({ layout = 'responsive', prefTypeOptions }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const pairedFieldBreakpoint = layout === 'single-column' ? 12 : 6;
  const referralSourceOptions = prefTypeOptions?.referral_source;
  const ratingOptions = prefTypeOptions?.account_rating;

  return (
    <Stack spacing={2}>
      <SubsectionTitle>Contact</SubsectionTitle>
      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Website">
            <TextField
              {...register('website')}
              fullWidth
              placeholder="acme.com"
              error={Boolean(errors.website)}
              helperText={errors.website?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Phone">
            <TextField
              {...register('phone_number')}
              fullWidth
              placeholder="(555) 000-0000"
              error={Boolean(errors.phone_number)}
              helperText={errors.phone_number?.message}
              autoComplete="tel"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Fax">
            <TextField
              {...register('fax_number')}
              fullWidth
              placeholder="(555) 000-0000"
              error={Boolean(errors.fax_number)}
              helperText={errors.fax_number?.message}
              autoComplete="tel"
            />
          </LabeledField>
        </Grid>

        {referralSourceOptions && (
          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Referral Source">
              <InlineRichOptionSelect
                name="referral_source"
                control={control}
                data={referralSourceOptions}
                error={errors.referral_source}
                placeholder="—"
              />
            </LabeledField>
          </Grid>
        )}

        {ratingOptions && (
          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Rating">
              <InlineRichOptionSelect
                name="rating"
                control={control}
                data={ratingOptions}
                error={errors.rating}
                placeholder="—"
              />
            </LabeledField>
          </Grid>
        )}

        <Grid item xs={12}>
          <LabeledField label="External ID">
            <TextField
              {...register('external_account_id')}
              fullWidth
              placeholder="ERP-00342"
              error={Boolean(errors.external_account_id)}
              helperText={errors.external_account_id?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateAccountContactFields;
